import { useNavigate, useParams } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ArrowLeft, Edit, Mail, Phone, MapPin, Calendar, Clock, MessageCircle } from 'lucide-react';
import { format } from 'date-fns';
import { UserCalendar } from '../components/UserCalendar';

export function UserDetailScreen() {
    const { id } = useParams();
    const navigate = useNavigate();

    const { data, isLoading } = trpc.getUserDetails.useQuery({ id: id! });

    if (isLoading) return <div className="p-8 text-center text-muted-foreground">Loading details...</div>;
    if (!data || !data.user) return <div className="p-8 text-center text-muted-foreground">User not found</div>;

    const { user, subscriptions } = data;
    const metadata = user.metadata ? JSON.parse(user.metadata) : {};

    return (
        <div className="min-h-screen bg-background pb-20">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md p-4 flex items-center justify-between border-b">
                <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                    <ArrowLeft className="h-5 w-5" />
                </Button>
                <div className="text-lg font-semibold truncate max-w-[200px]">{user.name}</div>
                <Button variant="ghost" size="icon" onClick={() => navigate(`/users/${id}/edit`)}>
                    <Edit className="h-5 w-5 text-primary" />
                </Button>
            </div>

            <div className="p-4 space-y-6">
                {/* Profile Header */}
                <div className="flex flex-col items-center text-center space-y-3">
                    <Avatar className="h-24 w-24 border-2 border-background shadow-lg">
                        <AvatarImage src={metadata.photo} />
                        <AvatarFallback className="text-2xl">{user.name.charAt(0)}</AvatarFallback>
                    </Avatar>

                    <div>
                        <h2 className="text-2xl font-bold">{user.name}</h2>
                        <div className="text-sm text-muted-foreground mt-1 flex items-center justify-center gap-2">
                            <Clock className="h-3 w-3" /> Joined {format(new Date(user.created_at || 0), 'PPP')}
                        </div>

                        {subscriptions[0] && new Date(subscriptions[0].end_date) >= new Date() && (
                            <Button
                                className="mt-4 gap-2 bg-green-600 hover:bg-green-700 text-white rounded-full"
                                onClick={() => {
                                    const sub = subscriptions[0];
                                    // Strip non-digits from phone
                                    const cleanPhone = user.phone.replace(/\D/g, '');
                                    const message = `Hello ${user.name}, your library subscription for Seat ${sub.seat_number} is ending on ${format(new Date(sub.end_date || ''), 'PPP')}. Please renew to continue using the facility.`;
                                    const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
                                    window.open(url, '_blank');
                                }}
                            >
                                <MessageCircle className="h-4 w-4" />
                                Remind Subscription
                            </Button>
                        )}
                    </div>
                </div>

                {/* Contact Info */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base">Contact Information</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3 text-sm">
                        <div className="flex items-center gap-3">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <span>{user.email}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <span>{user.phone}</span>
                        </div>
                        <div className="flex items-center gap-3">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span>{metadata.address || 'No address provided'}</span>
                        </div>
                    </CardContent>
                </Card>

                {/* Calendar */}
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base">Booking Calendar</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <UserCalendar subscriptions={subscriptions} />
                    </CardContent>
                </Card>

                {/* Subscriptions / Bookings History */}
                <div>
                    <h3 className="text-lg font-semibold px-1 mb-3">Subscription History</h3>
                    <div className="space-y-3">
                        {subscriptions.map((sub: any) => (
                            <Card key={sub.id} className="border-0 shadow-sm">
                                <CardContent className="p-4">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <div className="font-semibold">{sub.branch_name}</div>
                                            <div className="text-xs text-muted-foreground">
                                                Seat {sub.seat_number} • {sub.shift_name}
                                            </div>
                                        </div>
                                        {/* Status badge logic could be better, for now simple date check */}
                                        <div className={`text-xs px-2 py-1 rounded-full ${new Date(sub.end_date) >= new Date()
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-gray-100 text-gray-500'
                                            }`}>
                                            {new Date(sub.end_date) >= new Date() ? 'Active' : 'Expired'}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm">
                                        <Calendar className="h-3 w-3 opacity-50" />
                                        {format(new Date(sub.start_date), 'd MMM yyyy')} — {format(new Date(sub.end_date), 'd MMM yyyy')}
                                    </div>
                                    {sub.price && (
                                        <div className="mt-2 text-xs font-semibold">
                                            ₹{sub.price} paid
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        ))}
                        {subscriptions.length === 0 && (
                            <div className="p-4 text-center text-muted-foreground bg-muted/30 rounded-lg">
                                No booking history found.
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
